<script setup lang="ts">
/**
 * Доска проекта — макет 9:2: колонки 280 с шагом 16, горизонтальный скролл и
 * перемотка к дальнему краю.
 *
 * На телефоне (макет 27:2) колонок в ряд не бывает: статусы становятся полосой
 * пилюль со счётчиками, а под ней стоит одна выбранная колонка во всю ширину.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { useEventListener, useIntervalFn, useResizeObserver } from '@vueuse/core'
import { AlertTriangle, ChevronsLeft, ChevronsRight, Columns3, SearchX } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import ContextMenu from '@/components/ContextMenu.vue'
import InlinePicker from '@/components/InlinePicker.vue'
import IssueFilterBar from '@/components/IssueFilterBar.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ViewSwitch from '@/components/ViewSwitch.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import BoardColumn from '@/features/board/components/BoardColumn.vue'
import { emptyFilters, type BoardFilters, type BoardSort } from '@/features/board/api'
import { useBoardStore } from '@/features/board/store'
import {
  filtersFromQuery,
  filtersToQuery,
  hasListOnlyQuery,
  sharedQueryOf,
  SHARED_KEYS,
} from '@/features/issues/listState'

import type { LocationQuery } from 'vue-router'
import IssuePeek from '@/features/issue/components/IssuePeek.vue'
import CreateIssuePanel from '@/features/issues/components/CreateIssuePanel.vue'
import type { CreatedIssue } from '@/features/issues/api'
import { notifyIssueCreated } from '@/features/issues/created'

/** Как часто доска догоняет чужие переносы */
const REFRESH_INTERVAL = 30_000

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const board = useBoardStore()

const projectKey = computed(() => String(route.params.key ?? ''))

// Первая ступень — раздел проектов, вторая — сам проект. До этого путь
// начинался с проекта, а вёл его клик в список: название проекта уводило из
// проекта.
const breadcrumbs = computed(() => [
  { label: t('nav.projects'), to: '/projects' },
  { label: board.project?.name || projectKey.value, to: `/p/${projectKey.value}/board` },
  { label: t('views.board') },
])

const isMobile = useIsMobile()

/** Колонка, открытая на телефоне: по умолчанию первая по порядку доски. */
const activeColumnId = ref('')

const visibleColumns = computed(() => {
  if (!isMobile.value) return board.columns

  const active = board.columns.find((column) => column.id === activeColumnId.value)

  return active ? [active] : board.columns.slice(0, 1)
})

// Доска приехала (или сменился проект) — выбранная колонка могла исчезнуть.
watch(
  () => board.columns,
  (columns) => {
    if (!columns.some((column) => column.id === activeColumnId.value)) {
      activeColumnId.value = columns[0]?.id ?? ''
    }
  },
  { immediate: true },
)

const columnsRef = ref<HTMLElement | null>(null)
const isScrollable = ref(false)
const atEnd = ref(false)

const updateScroll = () => {
  const element = columnsRef.value

  if (!element) return

  const max = element.scrollWidth - element.clientWidth

  isScrollable.value = max > 1
  atEnd.value = element.scrollLeft >= max - 1
}

/** Доска шире экрана уже на пяти колонках: перемотка к дальнему краю. */
const jumpToEdge = () => {
  const element = columnsRef.value

  if (!element) return

  element.scrollTo({ left: atEnd.value ? 0 : element.scrollWidth, behavior: 'smooth' })
}

useEventListener(columnsRef, 'scroll', updateScroll, { passive: true })
useResizeObserver(columnsRef, updateScroll)

/**
 * Отбор доски живёт в URL и общий со списком (DEV-2040). Колонковый фильтр и
 * таб — часть отбора списка: доска их не применяет и честно говорит об этом
 * чипом, а не молчаливым игнором.
 */
const applyFromRoute = () => {
  const { filters } = filtersFromQuery(route.query)

  filters.columnIds = []

  if (JSON.stringify(filters) !== JSON.stringify(board.filters)) void board.applyFilters(filters)
}

/** Часть отбора из адреса, которую доска показать не может */
const hasInertFilter = computed(() => hasListOnlyQuery(route.query))

const onFilterChange = async (next: BoardFilters) => {
  // Свои ключи переписываем, чужие (`?issue=`, таб и колонка списка) — нет.
  const preserved: LocationQuery = {}

  for (const [key, value] of Object.entries(route.query)) {
    if (!(SHARED_KEYS as readonly string[]).includes(key)) preserved[key] = value
  }

  await router.replace({ query: { ...preserved, ...sharedQueryOf(filtersToQuery(next, 'open')) } })
}

watch(() => route.query, applyFromRoute)

const openBoard = async () => {
  applyFromRoute()
  await board.open(projectKey.value)

  // «Нет такого проекта» и «есть, но не для вас» — разные экраны, и различает
  // их только ответ сервера.
  const failureRoute = errorRoute(board.error, projectKey.value)

  if (failureRoute) await router.replace(failureRoute)
}

const onMove = async (issueId: string, columnId: string, beforeIssueId: string | undefined) => {
  try {
    await board.move(issueId, columnId, beforeIssueId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  }
}

const isCreateOpen = ref(false)
/** Из какой колонки позвали создавать; пусто — из шапки экрана */
const createColumnId = ref('')

const openCreate = (columnId = '') => {
  createColumnId.value = columnId
  isCreateOpen.value = true
}

// Доска перечитывается целиком: новая задача встаёт в конец своей колонки, а
// вписывать её в список руками значит гадать про позицию, которую только что
// назначил сервер. Видимой карточке отвечаем вспышкой и скроллом, а ключ и
// «Открыть» показывает общая плашка — она же единственный след задачи, легшей
// за пределы загруженного окна колонки (DEV-2055, DEV-2074).
const onCreated = async (issue: CreatedIssue) => {
  await board.refresh()

  notifyIssueCreated(issue.key)

  const card = document.querySelector(`[data-issue-id="${issue.id}"]`)

  if (!card) return

  board.highlight(issue.id)
  card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
}

// Обновление в фоне: соседний человек переносит карточку, и без него два
// экрана расходятся до перезагрузки страницы.
useIntervalFn(() => void board.refresh(), REFRESH_INTERVAL)

onMounted(openBoard)

// Переход между досками из сайдбара меняет только параметр маршрута — экран
// при этом не пересоздаётся.
watch(projectKey, openBoard)

/** Отказ правки из панели показываем тем же тостом, что и остальные */
const onPeekFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('issue.saveFailed')))
}

// ─────────────── режим сортировки колонок (DEV-2056) ───────────────

type SortChoice = 'manual' | BoardSort['field']

const sortOptions = computed(() =>
  (['manual', 'priority', 'due_date', 'updated_at'] as SortChoice[]).map((value) => ({
    value,
    label: t(`board.sort.${value}`),
  })),
)

const sortCaption = computed(() =>
  board.sort
    ? `${t('board.sort.label')}: ${t(`board.sort.${board.sort.field}`)}`
    : t('board.sort.label'),
)

/** Подпись бейджа в шапках колонок: «Приоритет ↓» */
const sortedLabel = computed(() =>
  board.sort ? `${t(`board.sort.${board.sort.field}`)} ${board.sort.desc ? '↓' : '↑'}` : '',
)

const onSortPick = async (choice: SortChoice) => {
  if (choice === 'manual') return board.setSort(null)

  // Приоритет смотрят сверху вниз, срок — ближайшим вперёд.
  await board.setSort({ field: choice, desc: choice === 'priority' || choice === 'updated_at' })
}

/** Тултип на попытку вертикального DnD — не чаще раза в пару секунд */
let blockedAt = 0

const onReorderBlocked = () => {
  const now = Date.now()

  if (now - blockedAt < 2000) return

  blockedAt = now
  toast.info(t('board.sort.reorderBlocked'))
}

// ─────────────── меню колонки (DEV-2055) ───────────────

const columnMenuRef = ref<InstanceType<typeof ContextMenu> | null>(null)
const menuColumnId = ref('')

const columnMenuItems = computed(() => [
  {
    key: 'collapse',
    label: board.collapsedColumns.has(menuColumnId.value)
      ? t('board.columnExpand')
      : t('board.columnCollapse'),
  },
  { key: 'open-list', label: t('board.columnAsList') },
])

const onColumnMenu = (columnId: string, x: number, y: number) => {
  menuColumnId.value = columnId
  columnMenuRef.value?.open(x, y)
}

const onColumnMenuSelect = (key: string) => {
  if (key === 'collapse') board.toggleColumnCollapsed(menuColumnId.value)

  // «Открыть списком» — замена «выделить все»: bulk живёт в списке, и колонка
  // открывается там с фильтром по себе (решение консилиума).
  if (key === 'open-list') {
    void router.push({
      path: `/p/${projectKey.value}/issues`,
      query: { column: menuColumnId.value, tab: 'all' },
    })
  }
}

</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="board.project">
      <!-- На телефоне переключателя вида и фильтров в шапке нет: в 390 пикселей
           они не встают, а без них экран остаётся рабочим (макет 27:2). -->
      <ViewSwitch v-if="!isMobile" :project-key="board.project.key" />

      <IssueFilterBar
        v-if="!isMobile"
        :filters="board.filters"
        :project-id="board.project.id"
        :type-profile="board.project.typeProfile"
        @change="onFilterChange"
      />

      <!-- Ссылка со списка может нести фильтр колонки или таб закрытых —
           у доски этих осей нет. Чип честнее молчания: иначе доска выглядит
           как «фильтр сломался» (DEV-2040). -->
      <span v-if="!isMobile && hasInertFilter" class="board__inert">
        {{ t('board.inertFilter') }}
      </span>

      <InlinePicker
        v-if="!isMobile"
        :options="sortOptions"
        :model-value="board.sort?.field ?? 'manual'"
        @update:model-value="onSortPick($event as SortChoice)"
      >
        <template #trigger="{ isOpen, toggle }">
          <button type="button" class="board__sort" :aria-expanded="isOpen" @click="toggle()">
            {{ sortCaption }}
          </button>
        </template>
      </InlinePicker>
      <!-- В панели кнопка короткая — «Создать» (макет 34:295): что именно
           создаётся, говорит экран, на котором она стоит. -->
      <AppButton variant="primary" size="small" @click="openCreate()">
        {{ t('toolbar.create') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="board">
    <div v-if="board.isLoading && !board.columns.length" class="board__columns">
      <div v-for="index in 4" :key="index" class="board__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="board.error"
      :icon="AlertTriangle"
      :title="t('board.error.title')"
      :description="t('board.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="openBoard">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="board.isEmpty && board.isFiltered"
      :icon="SearchX"
      :title="t('board.nothingFound.title')"
      :description="t('board.nothingFound.description')"
    >
      <AppButton variant="secondary" @click="board.applyFilters(emptyFilters())">
        {{ t('toolbar.filters.reset') }}
      </AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="board.isEmpty"
      :icon="Columns3"
      :title="t('board.empty.title')"
      :description="t('board.empty.description')"
    >
      <AppButton variant="primary" @click="openCreate()">
        {{ t('issues.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <!-- Пилюли статусов: на телефоне они заменяют ряд колонок. -->
    <div v-else-if="isMobile" class="board__mobile">
      <div class="board__pills">
        <button
          v-for="column in board.columns"
          :key="column.id"
          type="button"
          class="bpill"
          :class="{ 'bpill--active': column.id === activeColumnId }"
          :aria-pressed="column.id === activeColumnId"
          @click="activeColumnId = column.id"
        >
          {{ column.name }} · {{ column.issues.length }}
        </button>
      </div>

      <BoardColumn
        v-for="column in visibleColumns"
        :key="column.id"
        class="board__single"
        :column="column"
        :is-dragging="board.isDragging"
        :landed-id="board.landedId"
        :is-loading-more="board.loadingColumnId === column.id"
        :sorted-label="sortedLabel"
        :collapsed="board.collapsedColumns.has(column.id)"
        :project-key="projectKey"
        @move="onMove"
        @create="openCreate"
        @load-more="board.loadMoreColumn"
        @drag-start="board.beginDrag"
        @drag-end="board.endDrag"
        @menu="onColumnMenu"
        @clear-sort="board.setSort(null)"
        @reorder-blocked="onReorderBlocked"
      />
    </div>

    <div v-else ref="columnsRef" class="board__columns">
      <BoardColumn
        v-for="column in board.columns"
        :key="column.id"
        :column="column"
        :is-dragging="board.isDragging"
        :landed-id="board.landedId"
        :is-loading-more="board.loadingColumnId === column.id"
        :sorted-label="sortedLabel"
        :collapsed="board.collapsedColumns.has(column.id)"
        :project-key="projectKey"
        @move="onMove"
        @create="openCreate"
        @load-more="board.loadMoreColumn"
        @drag-start="board.beginDrag"
        @drag-end="board.endDrag"
        @menu="onColumnMenu"
        @clear-sort="board.setSort(null)"
        @reorder-blocked="onReorderBlocked"
      />
    </div>

    <button
      v-if="isScrollable"
      type="button"
      class="board__jump"
      :title="atEnd ? t('board.scrollToStart') : t('board.scrollToEnd')"
      :aria-label="atEnd ? t('board.scrollToStart') : t('board.scrollToEnd')"
      @click="jumpToEdge"
    >
      <ChevronsLeft v-if="atEnd" :size="18" />
      <ChevronsRight v-else :size="18" />
    </button>
  </div>

  <CreateIssuePanel
    v-if="board.project"
    v-model="isCreateOpen"
    :project-id="board.project.id"
    :type-profile="board.project.typeProfile"
    :column-id="createColumnId"
    @created="onCreated"
  />

  <ContextMenu ref="columnMenuRef" :items="columnMenuItems" @select="onColumnMenuSelect" />

  <IssuePeek @failed="onPeekFailed" />
</template>

<style scoped>
.board {
  /* Ровно остаток экрана под тулбаром: доска не скроллит страницу, скролл
     уходит внутрь — по горизонтали к колонкам, по вертикали внутрь колонки. */
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  overflow: hidden;
}

/* Телефон: полоса пилюль сверху, под ней одна колонка во всю ширину. */
.board__mobile {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  padding: 12px 12px 0;
}

.board__pills {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  overflow-x: auto;
  /* Полоса режется краем экрана — это и подсказывает, что её листают. */
  margin: 0 -12px;
  padding: 0 12px 2px;
  scrollbar-width: none;
}

.board__pills::-webkit-scrollbar {
  display: none;
}

.bpill {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  cursor: pointer;
}

.bpill--active {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-inverse, #fff);
}

.board__single {
  width: 100%;
  min-height: 0;
}

.board__columns {
  display: flex;
  flex: 1;
  gap: 16px;
  min-height: 0;
  /* 16 по кругу и 16 между колонками — как в макете: первая колонка стоит на
     x=16 доски, последняя не прижимается к правому краю. */
  padding: 16px;
  overflow-x: auto;
  overflow-y: hidden;
}

.board__columns::-webkit-scrollbar {
  height: 8px;
}

.board__columns::-webkit-scrollbar-thumb {
  border-radius: var(--radius-full);
  background: var(--color-border-strong);
}

.board__sort {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.board__sort:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

.board__inert {
  flex-shrink: 0;
  padding: 4px 8px;
  border: 1px dashed var(--color-border-strong);
  border-radius: var(--radius-sm);
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  white-space: nowrap;
}

.board__skeleton {
  flex-shrink: 0;
  width: 280px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.board__jump {
  position: absolute;
  right: 16px;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  box-shadow: var(--shadow-md);
  cursor: pointer;
}

.board__jump:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
</style>
